import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingScreen';
import { ApiRequestError } from '@/services/api';
import { invitationsService } from '@/services/users.service';
import { roleTitle } from '@/types/permission';
import { v } from '@/theme/tokens';

const MIN_PASSWORD_LENGTH = 10;

/** Reasons the server gives for a link that cannot be used, in plain Russian. */
const UNUSABLE: Record<string, string> = {
  INVITATION_NOT_FOUND: 'Ссылка недействительна. Попросите владельца выслать новую.',
  INVITATION_EXPIRED: 'Срок действия приглашения истёк. Попросите владельца выслать новую ссылку.',
  INVITATION_REVOKED: 'Приглашение отозвано. Обратитесь к владельцу.',
  INVITATION_ALREADY_ACCEPTED: 'Это приглашение уже использовано. Войдите со своим паролем.',
};

/**
 * «Вас пригласили в Ramen Ten Financial Control».
 *
 * Public by necessity — the person here has no account yet. The page shows
 * what the invitation grants (organisation, role, email) and takes only a
 * password: the email is fixed by the invitation and is never editable here,
 * so a link for one address cannot create an account for another.
 */
export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: ({ signal }) => invitationsService.preview(token, signal),
    enabled: Boolean(token),
    retry: false,
  });

  const field = (invalid: boolean) => ({
    width: '100%',
    padding: '11px 13px',
    border: `1px solid ${invalid ? v('negative') : v('line')}`,
    borderRadius: 10,
    margin: '6px 0 4px',
    fontSize: 14,
    fontFamily: 'inherit',
    background: v('surface'),
    color: v('ink'),
  });

  if (preview.isPending) {
    return (
      <AuthLayout title="Приглашение">
        <Spinner />
      </AuthLayout>
    );
  }

  if (preview.error) {
    const code = preview.error instanceof ApiRequestError ? preview.error.code : '';
    return (
      <AuthLayout title="Приглашение недействительно">
        <div style={{ fontSize: 13.5, color: v('ink-sub'), lineHeight: 1.6 }}>
          {UNUSABLE[code] ?? 'Не удалось открыть приглашение.'}
        </div>
        <Link to="/login" style={{ display: 'inline-block', marginTop: 16, fontSize: 13.5 }}>
          Перейти ко входу
        </Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Приглашение принято">
        <div style={{ fontSize: 13.5, color: v('ink-sub'), lineHeight: 1.6 }}>
          Учётная запись создана. Войдите с вашим email и новым паролем.
        </div>
        <Button
          variant="primary"
          style={{ width: '100%', marginTop: 16 }}
          onClick={() => navigate('/login', { replace: true })}
        >
          Войти
        </Button>
      </AuthLayout>
    );
  }

  const invitation = preview.data!;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Пароль должен быть не короче ${MIN_PASSWORD_LENGTH} символов`);
      return;
    }
    if (password !== confirm) {
      setError('Пароли не совпадают');
      return;
    }

    setSubmitting(true);
    try {
      await invitationsService.accept(token, password, confirm);
      setDone(true);
    } catch (e) {
      if (e instanceof ApiRequestError) {
        setError(UNUSABLE[e.code] ?? e.message);
      } else {
        setError('Не удалось принять приглашение');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Вас пригласили в Ramen Ten Financial Control">
      <div
        style={{
          background: v('panel'),
          border: `1px solid ${v('line')}`,
          borderRadius: 12,
          padding: '12px 14px',
          marginBottom: 18,
          fontSize: 13,
          lineHeight: 1.7,
        }}
      >
        <Row label="Организация" value={invitation.company_name} />
        <Row label="Роль" value={roleTitle(invitation.role.code)} />
        <Row label="Email" value={invitation.email} />
      </div>

      <form onSubmit={submit}>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 12.5, color: v('ink-sub') }}>Пароль</span>
          <input
            type="password"
            aria-label="Пароль"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={field(Boolean(error))}
          />
        </label>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 12.5, color: v('ink-sub') }}>Подтверждение пароля</span>
          <input
            type="password"
            aria-label="Подтверждение пароля"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={field(Boolean(error))}
          />
        </label>

        <div style={{ fontSize: 11.5, color: v('ink-muted'), marginBottom: 12 }}>
          Не короче {MIN_PASSWORD_LENGTH} символов.
        </div>

        {error && (
          <div role="alert" style={{ color: v('negative'), fontSize: 12.5, marginBottom: 10 }}>
            {error}
          </div>
        )}

        <Button type="submit" variant="primary" style={{ width: '100%' }} disabled={submitting}>
          {submitting ? 'Принимаем…' : 'Принять приглашение'}
        </Button>
      </form>
    </AuthLayout>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ color: v('ink-muted') }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}
